/* eslint-disable react-hooks/set-state-in-effect */
import {
  AccessTime as AccessTimeIcon,
  NotInterested as NotInterestedIcon,
  PersonAddAlt1 as PersonAddAlt1Icon,
  Assignment as AssignmentIcon,
  Cancel as CancelIcon,
  KeyboardArrowDown as KeyboardArrowDownIcon,
  Phone as PhoneIcon,
  Business as BusinessIcon,
  DirectionsBoat as DirectionsBoatIcon,
  Person as PersonIcon,
  WarningAmber as WarningAmberIcon,
} from "@mui/icons-material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import EditIcon from "@mui/icons-material/Edit";
import {
  Card,
  CardHeader,
  CardContent,
  CardActions,
  Box,
  Button,
  Grid,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Typography,
  Divider,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
} from "@mui/material";
import { styled, keyframes } from "@mui/material/styles";
import TextField from "@mui/material/TextField";
import { DateTimePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs, { type Dayjs } from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import timezone from "dayjs/plugin/timezone";
import utc from "dayjs/plugin/utc";
import { useContext, useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import AWSCognitoContext from "../../../contexts/aws-cognito-context";
import { deleteIncomingCallData } from "../../../store/slices/incomingCalls";
import { addNewCallsBlacklistsData } from "../../../store/thunks/callsBlacklistThunk";
import { createTaskThunk } from "../../../store/thunks/tasksThunks";
import { updateBerthRotationData } from "../../../store/thunks/voyageScheduleThunks";
import { getPositionColorCode } from "../../../utils/format-helper";
import PhoneUtils from "../../../utils/PhoneUtils";
import DeleteButton from "../../common/deleteButton";
import TaskDrawer from "../../tasks/TaskDrawer";
import AssignDrawer from "../AssignDrawer";

import ArrangementDetails from "./arragementDetails";
import Charterer from "./charterer";
import InfoField from "./infoField";
// import NotificationList from "./notificationList";
import NotificationAccordion from "./NotificationAccordion";
// import TagTranscription from "./tagTranscription";
import Suppliers from "./suppliers";
import VesselAccordion from "./VesselAccordion.tsx";
import VoyagesList from "./VoyagesList";

import type { AppDispatch, RootState } from "../../../store/store";
import type { CallData } from "../../../utils/types/incomingCall";
import type { CreateTaskPayload } from "../../../utils/types/tasks";

// Utility function to validate color values
const isValidColor = (color: any): boolean => {
  if (!color || typeof color !== "string") return false;
  if (/^#([0-9A-Fa-f]{3}){1,2}$/.test(color)) return true;
  if (/^rgba?\([\d\s,%]+\)$/.test(color)) return true;
  // MUI theme colors
  if (color.includes(".")) return true; // theme.palette.primary.main etc
  return false;
};

// Utility function to get safe color value with fallback
const getSafeColor = (color: any, fallback: string = "#999"): string => {
  return isValidColor(color) ? color : fallback;
};

// Constants
const TIMEZONE = "America/Chicago";
dayjs.extend(utc);
dayjs.extend(timezone);
dayjs.extend(relativeTime);

// Styled Components
const callAnimation = keyframes`
  0%, 50%, 100% { transform: rotate(0deg); }
  10%, 30% { transform: rotate(15deg); }
  20%, 40% { transform: rotate(-15deg); }
`;

const AnimatedCall = styled(Box)(({ theme }) => ({
  transform: "translateY(-50%)",
  animation: `${callAnimation} 2s infinite`,
  backgroundColor: theme.palette.success.main,
  borderRadius: "50%",
  width: 20,
  height: 20,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  boxShadow: theme.shadows[1],
}));

const ActionButton = styled(Button)({
  width: "100%",
  height: 36,
  padding: "0 16px",
  justifyContent: "space-between",
  color: "black",
  fontWeight: 500,
  "&:hover": { backgroundColor: "transparent" },
});

const RotatingArrow = styled(Box)<{ open: boolean }>(({ open }) => ({
  transition: "transform 0.3s ease",
  transform: open ? "rotate(180deg)" : "none",
  display: "flex",
  alignItems: "center",
}));

// UI Components
const Header = ({
  name,
  phone,
  active,
  person,
  company,
  vessel,
  vessels,
  contactType,
  agent,
  userRole,
}: {
  name?: string | null;
  phone?: string | null;
  active?: boolean | null;
  person?: any;
  company?: any;
  vessel?: any;
  vessels?: any[];
  contactType?: "people" | "company" | "vessel" | "blacklist" | "phone" | null;
  agent?: string | null;
  userRole?: string | null;
}) => {
  const { peopleList } = useSelector((state: RootState) => state.peopleList);

  // Function to get agent person details from peopleList
  const getAgentPersonDetails = (): { name: string | null; color: string } | null => {
    // If vessel has assignedAgentId, find the person
    if (vessel?.assignedAgentId) {
      const agentPerson = peopleList.find(
        (person) => person.personId === vessel.assignedAgentId
      );
      if (agentPerson) {
        return {
          name: agentPerson.name,
          color: agentPerson.preferences?.color || "",
        };
      }
    }

    // If company has companyId, find people with that company
    if (company?.companyId) {
      const assignedPerson = peopleList.filter(
        (person) => person.companyId === company.companyId
      );
      const assignedAgent = assignedPerson.find(
        (p) => p?.position?.value && p.position.value.toLowerCase() === "agent"
      );
      if (assignedAgent) {
        return {
          name: assignedAgent.name,
          color: assignedAgent.preferences?.color || "",
        };
      }
    }

    // Default if no agent found
    return null;
  };

  // Get agent details (prefer stored agent from vessel, fallback to found agent from company)
  const agentDetails = getAgentPersonDetails();
  const displayAgentName = agent || agentDetails?.name;
  const agentColor = agentDetails?.color || "";
  const renderContent = () => {
    // Case 1: Person Match (Highest Priority)
    if (person) {
      const positionValue = person.position;
      const color =
        person.preferences?.color ||
        (positionValue ? getPositionColorCode(positionValue) : undefined);
      return (
        <>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Person Match">
              <PersonIcon sx={{ color: "text.secondary", fontSize: 18 }} />
            </Tooltip>
            <Typography variant="body2" sx={{ fontWeight: '400' }} noWrap>
              {name}
            </Typography>
            {positionValue && (
              <Chip
                label={positionValue}
                size="small"
                sx={{
                  height: 20,
                  fontSize: "0.7rem",
                  backgroundColor: getSafeColor(color),
                  color: (theme) =>
                    theme.palette.getContrastText(getSafeColor(color)),
                }}
              />
            )}
          </Box>
          {person.company && (
            <Box
              sx={{ display: "flex", alignItems: "center", gap: 1, mt: 0.5 }}
            >
              <Tooltip title="Company">
                <BusinessIcon
                  sx={{ color: "text.secondary", fontSize: 16, ml: "4px" }}
                />
              </Tooltip>
              <Typography variant="caption" color="text.secondary" noWrap>
                {person?.company}
              </Typography>
            </Box>
          )}
        </>
      );
    }

    // Case 2: Company Match (No Person)
    if (company) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Company Match">
            <BusinessIcon sx={{ color: "text.secondary", fontSize: 18 }} />
          </Tooltip>
          <Typography variant="body2" sx={{ fontWeight: '400' }} noWrap>
            {name}
          </Typography>
          {company.type && (
            <Chip
              label={company.type}
              size="small"
              variant="outlined"
              sx={{ height: 20 }}
            />
          )}
        </Box>
      );
    }

    // Case 3: Vessel Match (No Person or Company)
    if (vessels) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title="Vessel Match">
            <DirectionsBoatIcon
              sx={{ color: "text.secondary", fontSize: 18 }}
            />
          </Tooltip>
          <Typography variant="body2" sx={{ fontWeight: '400' }} noWrap>
            {name}
          </Typography>
        </Box>
      );
    }

    // Case 4: Contact Type Match (Vessel, Blacklist, or Phone from contact search)
    if (contactType === "vessel" || contactType === "blacklist" || contactType === "phone") {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <Tooltip title={contactType === "vessel" ? "Vessel Contact" : contactType === "blacklist" ? "Blacklisted Number" : "Phone"}>
            {contactType === "vessel" ? (
              <DirectionsBoatIcon sx={{ color: "text.secondary", fontSize: 18 }} />
            ) : contactType === "blacklist" ? (
              <NotInterestedIcon sx={{ color: "error.main", fontSize: 18 }} />
            ) : (
              <PhoneIcon sx={{ color: "text.secondary", fontSize: 18 }} />
            )}
          </Tooltip>
          <Typography variant="body2" sx={{ fontWeight: '400' }} noWrap>
            {name}
          </Typography>
        </Box>
      );
    }

    // Case 5: No Match
    return (
      <Box sx={{ minWidth: 0 }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Tooltip title="Needs Association">
            <WarningAmberIcon sx={{ color: "warning.main", fontSize: 18, flexShrink: 0 }} />
          </Tooltip>
          <Typography variant="body2" sx={{ fontWeight: '400' }} noWrap>
            {name}
          </Typography>
        </Box>
      </Box>
    );
  };
  return <>
    <Stack direction="row" justifyContent="space-between" spacing={1}>
      <Stack>
        <Box sx={{ minWidth: 0 }}>{renderContent()}</Box>
        <Typography variant="subtitle2" color="text.secondary">
          {PhoneUtils.renderer(phone ?? "")}
        </Typography>
      </Stack>
      <Stack alignItems="flex-end">
        {displayAgentName && userRole?.toLocaleLowerCase() !== "agent" && (
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Agent">
              <Box
                sx={{
                  width: 28,
                  height: 28,
                  borderRadius: "50%",
                  backgroundColor: agentColor,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  fontSize: "0.7rem",
                  fontWeight: 600,
                  color: (theme) => theme.palette.getContrastText(agentColor),
                  flexShrink: 0,
                }}
              >
                {displayAgentName.charAt(0).toUpperCase()}
              </Box>
            </Tooltip>
            <Typography variant="body2" sx={{ fontWeight: '400' }} noWrap>
              {displayAgentName}
            </Typography>
          </Box>
        )}
        {active && (
          <AnimatedCall>
            <PhoneIcon sx={{ fontSize: 14, color: "white" }} />
          </AnimatedCall>
        )}
      </Stack>
    </Stack>
  </>
};

const DateInput = ({
  // label,
  value,
  onChange,
  disabled,
}: {
  label: string;
  value: Dayjs | null;
  onChange: (value: Dayjs | null) => void;
  disabled: boolean;
}) => (
  <LocalizationProvider dateAdapter={AdapterDayjs}>
    <DateTimePicker
      value={value}
      onChange={onChange}
      disabled={disabled}
      ampm={false}
      minutesStep={1}
      closeOnSelect={false}
      format="MMM D, YYYY HH:mm"
      timezone={TIMEZONE}
      slots={{ textField: TextField }}
      slotProps={{
        textField: {
          size: "small",
          fullWidth: true,
          sx: { minHeight: 28 },
        },
      }}
    />
  </LocalizationProvider>
);

const InfoSection = ({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) => (
  <>
    <Grid item xs={12} my={1}>
      <Divider>
        <Chip
          label={title}
          size="small"
          variant="outlined"
          sx={{ fontSize: "0.75rem", fontWeight: 500, color: "primary.main" }}
        />
      </Divider>
    </Grid>
    {children}
  </>
);

// Main Component
const CallCard = ({
  data,
  currentTime,
}: {
  data: CallData | null;
  currentTime: dayjs.Dayjs;
}) => {
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null);
  const [timeAnchor, setTimeAnchor] = useState<null | HTMLElement>(null);
  const [ata, setAta] = useState<Dayjs | null>(
    data?.vessel?.eta ? dayjs(data?.vessel?.eta) : null
  );
  const [atd, setAtd] = useState<Dayjs | null>(
    data?.vessel?.etd ? dayjs(data?.vessel?.etd) : null
  );
  const [assignDrawerOpen, setAssignDrawerOpen] = useState(false);
  const [taskDrawerOpen, setTaskDrawerOpen] = useState(false);
  const dispatch = useDispatch<AppDispatch>();
  const auth = useContext(AWSCognitoContext);

  const handleTimeEdit = (event: React.MouseEvent<HTMLElement>) => {
    setTimeAnchor(event.currentTarget);
    setMenuAnchor(null);
  };

  const handleTimesave = (_event: React.MouseEvent<HTMLElement>) => {
    if (!data?.jobId || !data?.vessel?.currentBerthId) return;
    dispatch(
      updateBerthRotationData({
        berth_id: data.vessel?.currentBerthId,
        job_id: data.jobId,
        data: {
          eta: ata ? ata.toISOString() : null,
          etd: atd ? atd.toISOString() : null,
        },
      })
    );
    setTimeAnchor(null);
  };

  const handleSubmitTask = (taskData: CreateTaskPayload) => {
    dispatch(createTaskThunk(taskData)).then(() => {
      setTaskDrawerOpen(false);
    });
  };

  const handleMarkAsTelemarketer = async () => {
    if (!data?.phone) return;

    try {
      await dispatch(
        addNewCallsBlacklistsData({
          data: {
            phone_number: data.phone,
            notes: "Marked as telemarketer from incoming call",
          },
        })
      ).unwrap();

      // Update the card by marking it as blacklist
      if (data) {
        data.contactType = "blacklist";
        data.name = "Blacklist";
      }

      setMenuAnchor(null);
    } catch (error) {
      console.error("Failed to mark as telemarketer:", error);
    }
  };

  useEffect(() => {
    setAta(data?.vessel?.eta ? dayjs(data?.vessel?.eta) : null);
    setAtd(data?.vessel?.etd ? dayjs(data?.vessel?.etd) : null);
  }, [data]);

  // const showActions =
  //   (!data?.company &&
  //     !data?.person &&
  //     (!data?.name || data?.name === "Unknown")) ||
  //   data?.person?.position === "Port Captain";
  const showActions = true;
  return (
    <>
      <Card
        sx={{
          width: 370,
          m: 3,
          borderLeft: `4px solid ${data?.contactType === "blacklist"
            ? "error.main"
            : getSafeColor(getPositionColorCode(data?.person?.position || ""), "#999")
            }`,
          backgroundColor:
            data?.contactType === "blacklist" ? "warning.lighter" : "background.paper",
        }}
      >
        <CardHeader title={<Header {...data} agent={data?.vessel?.agent} userRole={data?.person?.position} />} />

        {/* <InfoSection title="Tag call to user">
        <TagTranscription />
      </InfoSection> */}

        {showActions && (
          <CardActions>
            <Box width="100%" px={1}>
              <ActionButton
                variant="outlined"
                onClick={(e) => setMenuAnchor(e.currentTarget)}
                endIcon={
                  <RotatingArrow open={Boolean(menuAnchor)}>
                    <KeyboardArrowDownIcon />
                  </RotatingArrow>
                }
              >
                Actions
              </ActionButton>

              <Menu
                anchorEl={menuAnchor}
                open={Boolean(menuAnchor)}
                onClose={() => setMenuAnchor(null)}
              >
                {data?.person?.position === "Port Captain" &&
                  data.vessel?.currentStatus !== "Shifting" && (
                    <MenuItem onClick={handleTimeEdit}>
                      <ListItemIcon>
                        <EditIcon fontSize="small" color="primary" />
                      </ListItemIcon>
                      <ListItemText primary="Edit ATA/ATD" />
                    </MenuItem>
                  )}
                {(!data?.company || !data?.person) &&
                  (!data?.name || data?.name === "Unknown") && [
                    <MenuItem key="telemarketer" onClick={handleMarkAsTelemarketer}>
                      <ListItemIcon>
                        <NotInterestedIcon fontSize="small" color="error" />
                      </ListItemIcon>
                      <ListItemText primary="Mark as Telemarketer" />
                    </MenuItem>,

                    <MenuItem
                      key="associate-number"
                      onClick={() => {
                        setAssignDrawerOpen(true);
                        setMenuAnchor(null);
                      }}
                    >
                      <ListItemIcon>
                        <PersonAddAlt1Icon fontSize="small" color="success" />
                      </ListItemIcon>
                      <ListItemText primary="Associate Number" />
                    </MenuItem>,
                  ]} 
                    <MenuItem
                      key="create-task"
                      onClick={() => {
                        setTaskDrawerOpen(true);
                        setMenuAnchor(null);
                      }}
                    >
                      <ListItemIcon>
                        <AssignmentIcon fontSize="small" color="info" />
                      </ListItemIcon>
                      <ListItemText primary="Create Task" />
                    </MenuItem>
              </Menu>

              {/* <Menu
                anchorEl={timeAnchor}
                open={Boolean(timeAnchor)}
                onClose={() => setTimeAnchor(null)}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "left",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "left",
                }}
              >
                <Box p={2} width={320}>
                  <Stack spacing={2}>
                    <DateInput
                      label="ATA"
                      value={ata}
                      onChange={setAta}
                      disabled={false}
                    />
                    <DateInput
                      label="ATD"
                      value={atd}
                      onChange={setAtd}
                      disabled={false}
                    />
                    <Stack
                      direction="row"
                      spacing={1}
                      justifyContent="flex-end"
                    >
                      <Button
                        variant="text"
                        color="error"
                        onClick={() => setTimeAnchor(null)}
                        size="small"
                      >
                        Cancel
                      </Button>
                      <Button
                        variant="text"
                        onClick={handleTimesave}
                        size="small"
                      >
                        Save
                      </Button>
                    </Stack>
                  </Stack>
                </Box>
              </Menu> */}
              <Dialog
                open={Boolean(timeAnchor)}
                onClose={() => setTimeAnchor(null)}
                maxWidth="sm"
                fullWidth
              >
                <DialogTitle>Edit ATA/ATD Times</DialogTitle>
                <DialogContent>
                  <Stack spacing={3} sx={{ pt: 1 }}>
                    <DateInput
                      label="ATA"
                      value={ata}
                      onChange={setAta}
                      disabled={false}
                    />
                    <DateInput
                      label="ATD"
                      value={atd}
                      onChange={setAtd}
                      disabled={false}
                    />
                  </Stack>
                </DialogContent>
                <DialogActions>
                  <Button
                    variant="text"
                    color="error"
                    onClick={() => setTimeAnchor(null)}
                  >
                    Cancel
                  </Button>
                  <Button variant="contained" onClick={handleTimesave}>
                    Save
                  </Button>
                </DialogActions>
              </Dialog>
            </Box>
          </CardActions>
        )}

        {/* {data?.person?.position === "Port Captain" && ( */}
        {data?.contactType !== "blacklist" && data?.contactType !== null && (
          <CardContent>
            <InfoSection title={`Vessel Details${data?.vessels && data.vessels.length > 1 ? ` (${data.vessels.length})` : ""}`}>
              {data?.vessels && data.vessels.length > 0 ? (
                <VesselAccordion vessels={data.vessels} />
              ) : data?.vessel ? (
                <Grid container spacing={1}>
                  <Grid item xs={6}>
                    <InfoField label="Vessel" value={data.vessel?.vesselName} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoField label="Voyage" value={data.vessel?.voyage} />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoField
                      label="Operator"
                      valueJsx={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {data.vessel.appointedOperator ? (
                            <CheckCircleIcon
                              sx={{
                                fontSize: "14px",
                                color: "success.main",
                              }}
                            />
                          ) : (
                            <CheckCircleIcon
                              sx={{
                                color: "grey.500",
                                fontSize: "14px",
                              }}
                            />
                          )}
                          <Typography
                            variant="subtitle2"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            fontWeight={500}
                            title={data.vessel?.operator || "Unknown"}
                          >
                            {data.vessel?.operator || "Unknown"}
                          </Typography>
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoField
                      label="Management"
                      valueJsx={
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {data.vessel.appointedManagement ? (
                            <CheckCircleIcon
                              sx={{
                                fontSize: "14px",
                                color: "success.main",
                              }}
                            />
                          ) : (
                            <CheckCircleIcon
                              sx={{
                                color: "grey.500",
                                fontSize: "14px",
                              }}
                            />
                          )}
                          <Typography
                            variant="subtitle2"
                            overflow="hidden"
                            textOverflow="ellipsis"
                            whiteSpace="nowrap"
                            fontWeight={500}
                            title={data.vessel?.managementCompany || "Unknown"}
                          >
                            {data.vessel?.managementCompany || "Unknown"}
                          </Typography>
                        </Stack>
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoField
                      label="Current Berth"
                      value={
                        data.vessel?.currentBerth
                          ? data.vessel?.currentStatus === "Shifting"
                            ? `Shifting to ${data.vessel?.nextBerth}`
                            : data.vessel?.currentBerth
                          : "Upcoming"
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoField
                      label="Next Berth"
                      value={
                        data.vessel?.nextBerth ??
                        (data.vessel?.currentBerth
                          ? "Completed"
                          : "Not Available")
                      }
                    />
                  </Grid>
                  <Grid item xs={6}>
                    <InfoField
                      label="Status"
                      value={data.vessel?.currentStatus}
                    />
                  </Grid>
                </Grid>
              ) : (
                <Typography
                  variant="body2"
                  color="text.secondary"
                  textAlign="center"
                >
                  No data found
                </Typography>
              )}
            </InfoSection>
            {data?.arrangements && 
              (data.arrangements.deliveries?.length > 0 ||
               data.arrangements.bunkers?.length > 0 ||
               data.arrangements.onboardServices?.length > 0) && (
              <InfoSection title="Arrangement Details">
                <ArrangementDetails arrangements={data?.arrangements} />
              </InfoSection>
            )}
            {data?.arrangements?.bunkers && data.arrangements.bunkers.length > 0 && (
              <InfoSection title="Suppliers">
                <Suppliers suppliers={{ bunkers: data?.arrangements?.bunkers }} />
              </InfoSection>
            )}

            {data?.chartererInsights && data.chartererInsights.length > 0 && (
              <InfoSection title="Charterers">
                {data.chartererInsights.map((charterer) => (
                  <Charterer key={charterer.companyId} data={charterer} />
                ))}
              </InfoSection>
            )}

            {data?.notificationList && data?.notificationList.length > 0 && (
              <InfoSection title="Notifications List">
                <NotificationAccordion notifications={data?.notificationList} />
                {/* <NotificationList data={data?.persons} /> */}
              </InfoSection>
            )}
            {/* </CardContent> */}
            {/* )} */}

            {/* {data?.person?.position === "Surveyor" && ( */}
            {/* <CardContent> */}
            <InfoSection title="Voyages">
              <Box mt={1}>
                {data?.voyages?.length ? (
                  <VoyagesList voyages={data?.voyages} />
                ) : (
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    textAlign="center"
                  >
                    No data found
                  </Typography>
                )}
              </Box>
            </InfoSection>
          </CardContent>
        )}
        {/* )} */}

        <Stack
          direction="row"
          justifyContent="space-between"
          p={1}
          alignItems="center"
        >
          <Stack direction="row" alignItems="center" spacing={0.5}>
            <AccessTimeIcon
              sx={{ fontSize: "0.85rem", color: "text.secondary" }}
            />
            <Typography variant="caption" color="text.secondary">
              {dayjs(data?.time).from(currentTime)}
            </Typography>
          </Stack>
          <DeleteButton
            onConfirm={() => {
              dispatch(deleteIncomingCallData({ id: data?.id || "" }));
            }}
            text="Are you sure you want to hide this call insight"
            buttonName="Hide"
          >
            <IconButton size="small" color="error">
              <CancelIcon fontSize="small" />
            </IconButton>
          </DeleteButton>
          {/* <IconButton
          size="small"
          color="error"
          onClick={() => {
            dispatch(deleteIncomingCallData({ id: data.id || "" }));
          }}
        >
          <CancelIcon fontSize="small" />
        </IconButton> */}
        </Stack>
      </Card>
      <AssignDrawer
        open={assignDrawerOpen}
        phone={data?.phone || ""}
        onClose={() => setAssignDrawerOpen(false)}
      />
      <TaskDrawer
        open={taskDrawerOpen}
        onClose={() => setTaskDrawerOpen(false)}
        onSubmit={handleSubmitTask}
        initialData={{
          jobId: data?.jobId || undefined,
          agentId: auth?.user?.person_id || undefined,
        }}
      />
    </>
  );
};

export default CallCard;
